import React from 'react';

import ShareComposer from './ShareComposer';
import { buildReceiptShareText, buildReceiptShareUrl, formatReceiptTimestamp, type ReceiptMoment } from '../../lib/share';

interface ReceiptCardProps {
  officialId: string;
  officialName: string;
  officeName: string;
  moment: ReceiptMoment;
  shareEnabled?: boolean;
  active?: boolean;
  compact?: boolean;
  anchorable?: boolean;
  shareVariants?: { label: string; text: string }[];
}

const ReceiptCard: React.FC<ReceiptCardProps> = ({
  officialId,
  officialName,
  officeName,
  moment,
  shareEnabled = false,
  active = false,
  compact = false,
  anchorable = true,
  shareVariants,
}) => {
  const shareUrl = buildReceiptShareUrl({ officialId, anchorId: moment.anchorId });
  const shareText = buildReceiptShareText({ officialName, officeName, moment });
  const evidenceHref = anchorable ? `#${moment.anchorId}` : shareUrl;
  const containerClass = compact
    ? 'rounded-xl border p-4'
    : 'rounded-2xl border p-5';

  return (
    <article
      id={anchorable ? moment.anchorId : undefined}
      className={`${containerClass} ${active ? 'border-gold-500 bg-gold-500/10 shadow-lg shadow-gold-500/10' : 'border-cream-300 bg-white dark:border-navy-600 dark:bg-navy-800/40'}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-gold-600 dark:text-gold-400">Receipt</p>
          <h3 className="mt-1 text-sm font-black uppercase tracking-wide text-navy-900 dark:text-cream-50">{officialName}</h3>
          <p className="mt-1 text-[11px] text-navy-400 dark:text-navy-300">{officeName}</p>
        </div>
        <span className="rounded-full border border-cream-300 px-2 py-1 text-[9px] font-black uppercase tracking-widest text-navy-500 dark:border-navy-600 dark:text-cream-300">
          {moment.impactLabel}
        </span>
      </div>

      <p className="mt-4 text-sm font-bold leading-relaxed text-navy-900 dark:text-cream-50">{moment.claim}</p>
      <p className="mt-2 text-xs leading-relaxed text-navy-500 dark:text-cream-300">{moment.summary}</p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg bg-cream-100 px-3 py-3 dark:bg-navy-900/50">
          <p className="text-[9px] font-black uppercase tracking-widest text-navy-400 dark:text-navy-300">Evidence timestamp</p>
          <p className="mt-1 text-xs font-semibold text-navy-900 dark:text-cream-50">{formatReceiptTimestamp(moment.occurredAt)}</p>
        </div>
        <div className="rounded-lg bg-cream-100 px-3 py-3 dark:bg-navy-900/50">
          <p className="text-[9px] font-black uppercase tracking-widest text-navy-400 dark:text-navy-300">Evidence anchor</p>
          <a href={evidenceHref} className="mt-1 block text-xs font-semibold text-gold-600 hover:underline dark:text-gold-400">
            {moment.evidenceLabel}
          </a>
        </div>
      </div>

      {shareEnabled && (
        <div className="mt-4 border-t border-cream-200 pt-4 dark:border-navy-700">
          <ShareComposer shareUrl={shareUrl} shareText={shareText} evidenceUrl={moment.evidenceUrl} compact={compact} shareVariants={shareVariants} />
        </div>
      )}
    </article>
  );
};

export default ReceiptCard;
